import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import BlogRelatedStrip from '../../../components/feature/BlogRelatedStrip';
import BlogProgressBar from '../../../components/feature/BlogProgressBar';

const SITE_URL = 'https://hoosierlifeinsurance.com';

export default function LifeInsuranceQuotesIndiana() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // Set canonical URL
    const SITE_URL_VAL = 'https://hoosierlifeinsurance.com';
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.rel = 'canonical';
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = `${SITE_URL_VAL}/blog/life-insurance-quotes-indiana`;

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Life Insurance Quotes Indiana: Get Accurate Rates in Minutes (2025)',
        description: 'How to get accurate life insurance quotes in Indiana in 2025. What factors affect your rate, sample pricing by age and health, and how to compare quotes from multiple carriers.',
        author: { '@type': 'Person', name: 'Benjamin Wright' },
        publisher: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL },
        datePublished: '2025-02-22T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: `${SITE_URL}/blog/life-insurance-quotes-indiana`,
        mainEntityOfPage: 'https://hoosierlifeinsurance.com/blog/life-insurance-quotes-indiana',
        image: { '@type': 'ImageObject', url: 'https://public.readdy.ai/ai/img_res/3070836bf675b50c2168021ee9325853.jpg', width: 1200, height: 630 },
        timeRequired: 'PT10M',
        wordCount: 2000,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL
        },
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2', 'details summary']
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How do I get life insurance quotes in Indiana?',
            acceptedAnswer: { '@type': 'Answer', text: 'You can get quotes online, through a captive agent (one company only), or through an independent agent who shops multiple carriers. Independent agents typically find the best rates because they compare 10+ companies simultaneously.' },
          },
          {
            '@type': 'Question',
            name: 'What information do I need for a life insurance quote in Indiana?',
            acceptedAnswer: { '@type': 'Answer', text: 'You need your date of birth, height and weight, tobacco use history (last 5 years), any major health conditions, desired coverage amount, and desired policy length. You don\'t need your Social Security number for an initial quote.' },
          },
        ],
      },
    ];

    schemas.forEach((schema, idx) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-quotes-${idx}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach((_, idx) => {
        document.getElementById(`schema-quotes-${idx}`)?.remove();
      });
    };
  }, []);

  const ratingFactors = [
    { factor: 'Age', impact: 'Massive', icon: 'ri-calendar-line', detail: 'Every 5 years adds 40–80% to your premium. A 35-year-old pays roughly half of what a 45-year-old pays for the same policy.' },
    { factor: 'Health Class', impact: 'Massive', icon: 'ri-heart-pulse-line', detail: 'Preferred Plus, Preferred, Standard Plus, Standard, Substandard. Each tier differs by 20–40%. Controlled diabetes or high BP moves you down 1–2 tiers.' },
    { factor: 'Tobacco Use', impact: 'Very High', icon: 'ri-lungs-line', detail: 'Smokers pay 2–4× more than non-smokers. Even tobacco users 1 year ago are rated as current smokers by most carriers.' },
    { factor: 'Coverage Amount', impact: 'High', icon: 'ri-money-dollar-circle-line', detail: 'Larger policies are often cheaper per dollar of coverage. $1M costs about 70% more than $500K, not 100% more.' },
    { factor: 'Policy Length', impact: 'Moderate', icon: 'ri-time-line', detail: '30-year term costs 50–80% more than 20-year term. The longer the guarantee, the higher the premium.' },
    { factor: 'Gender', impact: 'Moderate', icon: 'ri-user-line', detail: 'Women pay 15–20% less than men for the same policy because of longer average life expectancy.' },
  ];

  const quoteComparison = [
    { name: 'Online quote tools', pros: 'Fast, anonymous', cons: 'Often inaccurate, show generic rates, can\'t account for health conditions', verdict: 'Good for ballpark' },
    { name: 'Captive agent (State Farm, etc.)', pros: 'Familiar brand, in-person', cons: 'Can only show one company\'s rates — not necessarily the best', verdict: 'Limited options' },
    { name: 'Independent agent (local)', pros: 'Shops 10+ carriers, free, same-day quotes', cons: 'Requires a short phone call or form', verdict: 'Best for accuracy & savings' },
    { name: 'Direct-to-consumer apps', pros: 'No-exam options, instant decision', cons: 'Rates 10–30% higher, limited coverage amounts (max $3M)', verdict: 'Good for convenience' },
  ];

  const sampleQuotes = [
    { profile: '28F, Preferred, Non-Smoker', policy: '$500K / 20yr', company1: '$13', company2: '$15', company3: '$19', savings: '$72/yr' },
    { profile: '35M, Standard, Non-Smoker', policy: '$750K / 20yr', company1: '$42', company2: '$48', company3: '$55', savings: '$156/yr' },
    { profile: '40F, Preferred, Non-Smoker', policy: '$1M / 20yr', company1: '$58', company2: '$65', company3: '$74', savings: '$192/yr' },
    { profile: '45M, Standard+, Non-Smoker', policy: '$500K / 15yr', company1: '$82', company2: '$91', company3: '$99', savings: '$204/yr' },
    { profile: '52F, Standard, Non-Smoker', policy: '$250K / 10yr', company1: '$68', company2: '$77', company3: '$88', savings: '$240/yr' },
  ];

  return (
    <>
      <title>Life Insurance Quotes Indiana | Compare Rates 2026 | Hoosier Life</title>
      <meta name="description" content="Get accurate life insurance quotes in Indiana in 2026. What affects your rate, real pricing by age and health class, and how to compare quotes from 20+ carriers at once — free." />
      <meta name="keywords" content="life insurance quotes Indiana, Indiana life insurance rates, get life insurance quote Indianapolis, compare life insurance Indiana 2025" />
      <meta property="og:title" content="Life Insurance Quotes Indiana: Compare Accurate Rates (2025)" />
      <meta property="og:description" content="What affects your rate, real sample pricing, and how to compare quotes from 10+ Indiana carriers at once." />
      <meta property="og:url" content={`https://hoosierlifeinsurance.com/blog/life-insurance-quotes-indiana`} />
      <link rel="canonical" href="https://hoosierlifeinsurance.com/blog/life-insurance-quotes-indiana" />
      <meta name="robots" content="index, follow" />
      <meta property="og:type" content="article" />
      <meta property="og:image" content="https://readdy.ai/api/search-image?query=Indiana%20insurance%20agent%20comparing%20life%20insurance%20quotes%20on%20computer%20screen%20bright%20professional%20office%20Indianapolis%20clean%20minimal%20background%20showing%20rate%20comparison%20charts&width=1200&height=630&seq=blog021og&orientation=landscape" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content="https://readdy.ai/api/search-image?query=Indiana%20insurance%20agent%20comparing%20life%20insurance%20quotes%20on%20computer%20screen%20bright%20professional%20office%20Indianapolis%20clean%20minimal%20background%20showing%20rate%20comparison%20charts&width=1200&height=630&seq=blog021og&orientation=landscape" />

      <BlogProgressBar />
      <Header />

      <article className="min-h-screen bg-white">
        <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white pt-28 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <Link to="/blog" className="inline-flex items-center text-white/60 hover:text-[#D97642] mb-6 transition-colors text-sm">
              <i className="ri-arrow-left-line mr-2"></i>Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-[#D97642]/20 text-[#D97642] text-xs font-bold px-3 py-1.5 rounded-full">Term Life</span>
              <span className="text-white/50 text-xs">10 min read · March 2025</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">Life Insurance Quotes Indiana: Get Accurate Rates in Minutes</h1>
            <p className="text-white/75 text-xl max-w-2xl leading-relaxed">What factors determine your quote, real 2025 sample pricing, and the fastest way to compare Indiana carriers side-by-side.</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-12">

          <div className="bg-[#1A3A52]/5 border-l-4 border-[#1A3A52] p-6 rounded-r-xl mb-10">
            <p className="font-bold text-gray-900 mb-1 flex items-center gap-2"><i className="ri-information-line text-[#D97642]"></i>Why Online Quotes Are Often Wrong</p>
            <p className="text-gray-700 leading-relaxed">Most life insurance quote websites show "teaser" rates for the healthiest applicants. Your real rate depends on age, health class, and which carrier sees your profile most favorably. Before getting quotes, use our <Link to="/blog/how-much-life-insurance-indiana" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">Indiana coverage calculator</Link> to confirm how much coverage you need — then compare quotes for that exact amount.</p>
          </div>

          {/* Key Takeaways */}
          <div className="bg-[#1A3A52] text-white rounded-2xl p-7 mb-10">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <i className="ri-bookmark-3-fill text-[#D97642] text-xl"></i>
              Key Takeaways
            </h2>
            <ul className="space-y-2.5">
              {[
                'Online quote tools typically show "teaser" rates for the healthiest applicants — your real rate depends on age, health class, and which specific carrier sees your profile most favorably',
                'The same $500K/20yr term policy can cost $13/month from one Indiana carrier and $19/month from another for the identical applicant — a 46% difference, $72/year in savings',
                'Six factors determine your Indiana life insurance quote: age (biggest impact), health class, tobacco use, coverage amount, policy length, and gender',
                'An independent agent shops 10+ carriers simultaneously — captive agents (State Farm, Allstate) can only show you their own company\'s rates',
                'You don\'t need your Social Security number for an initial quote — just date of birth, height/weight, tobacco use, major health conditions, and desired coverage',
              ].map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                  <i className="ri-check-line text-[#D97642] mt-0.5 flex-shrink-0"></i>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* 6 factors */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">What Determines Your Life Insurance Quote in Indiana</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Your premium is determined by 6 factors in rough order of impact: (1) Age — every 5-year band adds 40–80%, (2) Health class — from Preferred Plus to Standard is a 50%+ difference, (3) Tobacco use — smokers pay 3× more, (4) Coverage amount — larger policies cost less per dollar, (5) Policy length — 30yr term costs 50–80% more than 20yr, (6) Gender — women pay 15–20% less. You can control factors 2–4 before applying.
            </div>
            <p className="text-gray-600 mb-6">Six factors decide your premium — some you can control, some you can't:</p>
            <div className="space-y-3">
              {ratingFactors.map((f, i) => (
                <div key={i} className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1A3A52]/5 rounded-lg flex-shrink-0">
                    <i className={`${f.icon} text-[#1A3A52] text-lg`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span className="font-bold text-gray-900">{f.factor}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${f.impact === 'Massive' ? 'bg-red-100 text-red-600' : f.impact === 'Very High' ? 'bg-orange-100 text-orange-600' : 'bg-amber-100 text-amber-600'}`}>
                        {f.impact} impact
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm">{f.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Sample quotes */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Real Indiana Quotes: Same Policy, 3 Carriers (2025)</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Shopping multiple carriers is the most impactful thing you can do to lower your quote. A 35-year-old Indiana male paying $42/month from one carrier might pay $48/month at another and $55/month at a third for identical coverage. That's a $156/year difference — $3,120 over a 20-year policy — for zero difference in coverage.
            </div>
            <p className="text-gray-600 mb-2">This is why shopping multiple carriers matters. All prices are for the same coverage — the only variable is the carrier:</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-4 py-3 text-left font-semibold">Applicant Profile</th>
                    <th className="px-4 py-3 text-left font-semibold">Policy</th>
                    <th className="px-4 py-3 text-left font-semibold">Carrier A</th>
                    <th className="px-4 py-3 text-left font-semibold">Carrier B</th>
                    <th className="px-4 py-3 text-left font-semibold">Carrier C</th>
                    <th className="px-4 py-3 text-left font-semibold text-emerald-300">Annual Savings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sampleQuotes.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 font-medium text-gray-900 text-xs">{r.profile}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{r.policy}</td>
                      <td className="px-4 py-3 text-emerald-600 font-bold">${r.company1}</td>
                      <td className="px-4 py-3 text-gray-700 font-semibold">${r.company2}</td>
                      <td className="px-4 py-3 text-gray-500">${r.company3}</td>
                      <td className="px-4 py-3 text-emerald-600 font-bold">{r.savings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">Carrier A = lowest available Indiana rate. Carrier C = highest of the 3. Monthly premiums. Updated January 2025.</p>
          </section>

          {/* Where to get quotes */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">4 Ways to Get Life Insurance Quotes in Indiana</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> The best way to get accurate Indiana life insurance quotes is through an independent agent who shops 10+ carriers using your real health profile. Online tools show ballpark estimates only. Captive agents (State Farm, Allstate) show only one company. Direct-to-consumer apps offer speed but cost 10–30% more and have limited coverage amounts.
            </div>
            <p className="text-gray-600 mb-4">For context on what a good rate looks like before you shop, see our <Link to="/blog/life-insurance-rates-indianapolis" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">Indianapolis life insurance rates guide</Link> and our <Link to="/blog/cheapest-life-insurance-indiana" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">guide to the cheapest Indiana options</Link>.</p>
            <div className="space-y-4">
              {quoteComparison.map((q, i) => (
                <div key={i} className={`border rounded-xl p-5 ${i === 2 ? 'border-[#D97642] bg-[#D97642]/5' : 'border-gray-200 bg-white'}`}>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h3 className="font-bold text-gray-900">{q.name}</h3>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${i === 2 ? 'bg-[#D97642] text-white' : 'bg-gray-100 text-gray-600'}`}>{q.verdict}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-emerald-600 font-semibold mb-1">Pros</p>
                      <p className="text-gray-600">{q.pros}</p>
                    </div>
                    <div>
                      <p className="text-red-500 font-semibold mb-1">Cons</p>
                      <p className="text-gray-600">{q.cons}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* What to have ready */}
          <section className="mb-12 bg-gray-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What You Need for an Indiana Life Insurance Quote</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { info: 'Date of birth', required: true },
                { info: 'Height and current weight', required: true },
                { info: 'Tobacco use in the last 5 years', required: true },
                { info: 'Major health conditions (diabetes, heart disease, cancer history)', required: true },
                { info: 'Desired coverage amount', required: true },
                { info: 'Desired policy length (10, 15, 20, 30 years)', required: true },
                { info: 'Social Security number', required: false },
                { info: 'Medical records', required: false },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <i className={`${item.required ? 'ri-checkbox-circle-fill text-[#D97642]' : 'ri-checkbox-blank-circle-line text-gray-400'}`}></i>
                  <span className={item.required ? 'text-gray-800 font-medium' : 'text-gray-500'}>
                    {item.info} {!item.required && <span className="text-xs">(not needed for initial quote)</span>}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-center text-white mb-12">
            <h2 className="text-2xl font-bold mb-3">Get Your Accurate Indiana Quote Now</h2>
            <p className="text-white/70 mb-6 max-w-xl mx-auto">We'll compare 10+ carriers using your actual health profile and have real, accurate quotes ready in under 10 minutes.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/instant-quote" className="px-6 py-3 bg-[#D97642] text-white rounded-lg font-bold hover:bg-[#C26638] transition-colors whitespace-nowrap">Get Accurate Quote</Link>
              <Link to="/book-appointment" className="px-6 py-3 bg-white/10 text-white rounded-lg font-bold hover:bg-white/20 border border-white/20 transition-colors whitespace-nowrap">Schedule a Review</Link>
            </div>
          </section>

          {/* Author Bio */}
          <div className="border border-gray-200 rounded-2xl p-7 my-8 flex items-start gap-5">
            <div className="w-14 h-14 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0">
              <i className="ri-shield-user-line text-white text-2xl"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 text-base mb-1">Written by the Licensed Life Insurance Specialists at Hoosier Life Insurance</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">Our licensed Indiana agents compare real quotes from 10+ carriers using your actual health profile — not teaser rates. We have access to the same carrier quoting systems used by all licensed Indiana agents, so you get genuinely accurate pricing before you apply.</p>
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Licensed in Indiana</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Independent — 20+ Carriers</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Last reviewed March 2025</span>
              </div>
            </div>
          </div>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Related Guides</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { to: '/blog/cheapest-life-insurance-indiana', icon: 'ri-funds-line', title: 'Cheapest Options', desc: 'How to pay less for the same coverage' },
                { to: '/blog/life-insurance-rates-indianapolis', icon: 'ri-price-tag-3-line', title: 'Rate Guide 2025', desc: 'Full pricing by age and health class' },
                { to: '/blog/best-life-insurance-indianapolis', icon: 'ri-star-line', title: 'Best Carriers Ranked', desc: 'Top 10 Indiana companies compared' },
              ].map(r => (
                <Link key={r.to} to={r.to} className="group border border-gray-200 rounded-xl p-5 hover:border-[#D97642] transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1A3A52]/5 rounded-lg mb-3">
                    <i className={`${r.icon} text-[#1A3A52] text-xl`}></i>
                  </div>
                  <p className="font-bold text-gray-900 text-sm mb-1 group-hover:text-[#D97642] transition-colors">{r.title}</p>
                  <p className="text-gray-500 text-xs">{r.desc}</p>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </article>

      <BlogRelatedStrip currentSlug="life-insurance-quotes-indiana" />
      <Footer />
    </>
  );
}
